import React, {useEffect, useRef, useState} from 'react';
import FormInput from "../../formComponents/FormInput";
import FormBlock from "../../formComponents/FormBlock";
import BaseSimpleItemEditorForm from "../BaseSimplePage/BaseSimpleItemEditorForm";
import FormImage from "../../formComponents/FormImage";
import FormCheckbox from "../../formComponents/FormCheckbox";
import FormMultilineInput from "../../formComponents/FormMultilineInput";
import FormCheckboxGrid from "../../formComponents/FormCheckboxGrid";
import FormDatePeriodInput from "../../formComponents/FormDatePeriodInput";
import FormLinkedItemListSelect from "../../formComponents/FormLinkedItemListSelect";
import FormMuseumEventVisitsEditor from "./FormMuseumEventVisitsEditor";
import {clientDateToServerDate, serverDateToClientDate} from "../../utils/DateTimeUtil";
import FormUrlInput from "../../formComponents/FormUrlInput";
import {getFullName, getShortName} from "../../utils/PersonNameUtil";
import FormLinkedItemSelect from "../../formComponents/FormLinkedItemSelect";
import FormLinkedItemSimpleSelect from "../../formComponents/FormLinkedItemSimpleSelect";
import {allAges, allArtistRoles, allDayOfWeeks, allSchoolClasses} from "../../data/Enums";
import FormRewardsSelect from "../../formComponents/FormRewardsSelect";

const EventsMuseumItemForm = ({initData, controlRef, pageOptions, readOnlyMode, draftData}) => {

    const controlRefName = useRef(null);
    const controlRefDescription = useRef(null);
    const controlRefCover = useRef(null);

    const controlRefCity = useRef(null);
    const controlRefLocation = useRef(null);
    const controlRefCustomLocation = useRef(null);

    const controlRefExpositionType = useRef(null);
    const controlRefTemporaryExpositionDates = useRef(null);
    const controlRefSeasonalExpositionDates = useRef(null);
    const controlRefOffDays = useRef(null);
    const controlRefArtists = useRef(null);
    const controlRefLiteraryWorks = useRef(null);
    const controlRefHistFigures = useRef(null);
    const controlRefSpheres = useRef(null);
    const controlRefTags = useRef(null);
    const controlRefIsSchoolProgram = useRef(null);
    const controlRefSchoolClasses = useRef(null);
    const controlRefSchoolClass = useRef(null);
    const controlRefSchoolSubject = useRef(null);
    const controlRefSchoolTheme = useRef(null);
    const controlRefMinAge = useRef(null);
    const controlRefIsPushkinCardAccepted = useRef(null);
    const controlRefAwards = useRef(null);
    const controlRefOlympiads = useRef(null);
    const controlRefFestivals = useRef(null);
    const controlRefUrl = useRef(null);
    const controlRefVisitTypes = useRef(null);
    const controlRefArchived = useRef(null);

    const [selectedCityId, setSelectedCityId] = useState(initData?.refs?.locations?.[initData?.location_id ?? null]?.city_id);
    const [selectedLocationId, setSelectedLocationId] = useState(initData?.location_id);
    const [customLocationIncluded, setCustomLocationIncluded] = useState(Boolean(initData?.custom_location));

    const initExpositionType = initData?.museum_details?.exposition_type ?? null;
    const [expositionType, setExpositionType] = useState(initExpositionType);
    const [noExpoDates, setNoExpoDates] = useState(getInitialNoExpoDates(initData));
    const [isSchoolProgram, setIsSchoolProgram] = useState(getInitialIsSchoolProgram(initData))
    const [schoolClassId, setSchoolClassId] = useState(getInitialSchoolClassId(initData));
    const [schoolSubjectId, setSchoolSubjectId] = useState(getInitialSchoolSubjectId(initData))
    const [schoolThemeId, setSchoolThemeId] = useState(getInitialSchoolThemeId(initData))

    useEffect(() => {
        if (controlRef) {
            controlRef.current = {
                validate: validate,
                getData: getData,
            };
        }
    });

    function validate() {
        let isValid = true;

        if (!controlRefName.current.validateNotEmpty("Нужно указать название!"))
            isValid = false;

        if (!controlRefName.current.validateLength(500))
            isValid = false;

        if (!controlRefDescription.current.validateLength(2000))
            isValid = false;

        if (!controlRefCity.current.validateIsSelected("Нужно выбрать город!"))
            isValid = false;

        if (selectedCityId && !controlRefLocation.current.validateIsSelected("Нужно выбрать локацию!"))
            isValid = false;

        if (customLocationIncluded) {
            if (!controlRefCustomLocation.current.validateNotEmpty("Нужно указать место проведения (или убрать галку 'Событие на внешней площадке')!"))
                isValid = false;
            else if (!controlRefCustomLocation.current.validateLength(500))
                isValid = false;
        }

        if (!controlRefUrl.current.validateNotEmpty("Нужно указать сайт!"))
            isValid = false;
        else if (!controlRefUrl.current.validateUrl("Неверно указан адрес сайта!"))
            isValid = false;
        else if (!controlRefUrl.current.validateLength(2000))
            isValid = false;

        if (!controlRefAwards.current.validateYears())
            isValid = false;
        if (!controlRefOlympiads.current.validateYears())
            isValid = false;
        if (!controlRefFestivals.current.validateYears())
            isValid = false;

        if (!controlRefExpositionType.current.validateIsSelected("Нужно выбрать время экспозиции!"))
            isValid = false;

        if (expositionType === "temporary" && !noExpoDates) {
            if (!controlRefTemporaryExpositionDates.current.validateNotEmpty("Нужно указать даты или выбрать \"Даты выставки не указаны\" ниже!"))
                isValid = false;
            else if (!controlRefTemporaryExpositionDates.current.validateDates("Неверно указаны даты! Укажите обе даты в формате ДД.ММ.ГГГГ ; Также убедитесь, что дата начала не позже по времени, чем дата окончания. (если даты не указаны, выберите флажок ниже)"))
                isValid = false;
        } else if (expositionType === "seasonal") {
            if (!controlRefSeasonalExpositionDates.current.validateNotEmpty("Нужно указать даты!"))
                isValid = false;
            else if (!controlRefSeasonalExpositionDates.current.validateDates("Неверно указаны даты! Укажите обе даты в формате ДД.ММ"))
                isValid = false;
        }

        if (!controlRefSpheres.current.validateNotEmpty("Нужно указать хотя бы одну сферу!"))
            isValid = false;

        if (isSchoolProgram) {
            if (!controlRefSchoolClass.current.validateIsSelected("Нужно выбрать класс!"))
                isValid = false;

            if (!controlRefSchoolSubject.current.validateIsSelected("Нужно выбрать предмет!"))
                isValid = false;

            if (schoolSubjectId && schoolClassId) {
                if (!controlRefSchoolTheme.current.validateIsSelected("Нужно выбрать тему (или выбрать \"не указана\")!"))
                    isValid = false;
            }
        }

        if (!controlRefMinAge.current.validateIsSelected("Нужно выбрать возрастное ограничение!"))
            isValid = false;

        if (!controlRefVisitTypes.current.validateNotEmpty("Нужно указать хотя бы один вариант посещения!"))
            isValid = false;
        else if (!controlRefVisitTypes.current.validateItems())
            isValid = false;

        return isValid;
    }

    function getData() {
        return {
            kultural_goal: pageOptions.kultural_goal,
            type_id: 3, // TODO!
            name: controlRefName.current.getText(),
            description: controlRefDescription.current.getText(),
            cover_base64: controlRefCover.current.isChangedToImage() ? controlRefCover.current.getBase64() : null,
            clear_cover: controlRefCover.current.isChangedToCleared() ? true : null,
            location_id: selectedLocationId,
            custom_location: customLocationIncluded ? controlRefCustomLocation.current.getText() : null,
            exposition_type: expositionType,
            exposition_start: expositionType === "temporary" ?
                (noExpoDates ? null : clientDateToServerDate(controlRefTemporaryExpositionDates.current.getFromDate())) :
                expositionType === "seasonal" ?
                    clientDateToServerDate(`${controlRefSeasonalExpositionDates.current.getFromDate()}.2000`) : null,
            exposition_end: expositionType === "temporary" ?
                (noExpoDates ? null : clientDateToServerDate(controlRefTemporaryExpositionDates.current.getTillDate())) :
                expositionType === "seasonal" ?
                    clientDateToServerDate(`${controlRefSeasonalExpositionDates.current.getTillDate()}.2000`) : null,
            off_days: controlRefOffDays.current.getCheckedIds(),
            artists: getArtistsData(),
            literary_work_ids: controlRefLiteraryWorks.current.getSelectedIds(),
            sphere_ids: controlRefSpheres.current.getSelectedIds(),
            tag_ids: controlRefTags.current.getSelectedIds(),
            is_for_toddlers: controlRefSchoolClasses.current.getPreExtraItemChecked(),
            school_classes: controlRefSchoolClasses.current.getCheckedIds(),
            is_for_adults: controlRefSchoolClasses.current.getPostExtraItemChecked(),
            is_school_program: isSchoolProgram,
            school_class: isSchoolProgram && !schoolThemeId ? schoolClassId : null,
            school_subject_id: isSchoolProgram ? schoolSubjectId : null,
            school_theme_id: isSchoolProgram ? schoolThemeId : null,
            min_age: getAgeData(controlRefMinAge),
            is_pushkin_card_accepted: controlRefIsPushkinCardAccepted.current.getChecked(),
            reward_years: getRewardYearsData(),
            url: controlRefUrl.current.getUrl(),
            visit_types: controlRefVisitTypes.current.getItems(),
            is_deleted: initData && controlRefArchived.current.getChecked(),
        }
    }

    function getRewardYearsData() {
        const options = [...controlRefAwards.current.getSelectedOptions(), ...controlRefOlympiads.current.getSelectedOptions(), ...controlRefFestivals.current.getSelectedOptions()];
        return options.map(x => ({reward_id: x.id, year_start: x.year1, year_end: x.year2}));
    }

    function getArtistsData() {
        return [
            ...controlRefArtists.current.getSelectedIds().map(x => ({id: x, role: "painter"})), // TODO: omit role
            ...controlRefHistFigures.current.getSelectedIds().map(x => ({id: x, role: "historical_figure"})),
        ];
    }

    function renderInitialLiteraryWorkItemExtraInfo(item, className) {
        return <span className={className}>{getInitialAuthorName(item.id)}</span>
    }

    function getInitialAuthorName(literaryWorkId) {
        const work = initData?.refs?.literary_works?.[literaryWorkId];
        if (!work)
            return "?";

        if (work.author_id === null)
            return "(без автора)";

        const author = initData?.refs?.artists?.[work.author_id];
        return author ? getShortName(author.first_name, author.last_name, author.middle_name) : "?";
    }

    function renderInitialArtistItemExtraInfo(item, className) {
        return <span className={className}>{getInitialArtistMainRoleName(item.id)}</span>
    }

    function getInitialArtistMainRoleName(artistId) {
        const artist = initData?.refs?.artists?.[artistId];
        if (!artist)
            return "?";

        return allArtistRoles.find(x => x.id === artist.main_role)?.name ?? "?";
    }

    function renderInitialSchoolThemeItemExtraInfo(item, className) {
        return <span className={className} style={{textWrap: "nowrap"}}>{getInitialSchoolThemeClassName(item.id)}</span>
    }

    function getInitialSchoolThemeClassName(schoolThemeId) {
        const schoolTheme = initData?.refs?.school_themes?.[schoolThemeId];
        if (!schoolTheme)
            return "?";

        const classItem = allSchoolClasses.find(x => x.id === schoolTheme.class);
        return classItem ? classItem.name : "?";
    }

    return (
        <BaseSimpleItemEditorForm>
            <FormBlock title="Основные">
                <FormInput
                    controlRef={controlRefName}
                    label="Название"
                    widthLimit={500}
                    initValue={draftData?.name ?? (initData?.name ?? "")}
                    readOnly={readOnlyMode}
                    infoText={draftData?.name}
                />
                <FormMultilineInput
                    controlRef={controlRefDescription}
                    label="Краткое описание"
                    widthLimit={700}
                    initValue={initData?.description ?? ""}
                    maxVisibleRows={6}
                    readOnly={readOnlyMode}
                />
                <FormImage
                    controlRef={controlRefCover}
                    label="Постер"
                    initImageUrl={initData?.cover_url ?? ""}
                    imageViewSize={250}
                    imageCanvasMaxSize={250}
                    readOnly={readOnlyMode}
                />
            </FormBlock>
            <FormBlock title="Организатор и место проведения">
                <FormLinkedItemSelect
                    controlRef={controlRefCity}
                    label="Город (организатор)"
                    widthLimit={500}
                    initSelectedId={selectedCityId}
                    initRefList={initData?.refs?.cities}
                    apiName2="cities"
                    showIds={true}
                    onSelect={id => {
                        setSelectedCityId(id);
                        setSelectedLocationId(undefined);
                    }}
                    renderItemExtraInfoFunc={renderCityItemExtraInfo}
                    readOnly={readOnlyMode}
                    infoText={draftData?.city}
                />
                <FormLinkedItemSelect
                    locked={!Boolean(selectedCityId)}
                    lockedMessage={"Сначала нужно выбрать город!"}
                    controlRef={controlRefLocation}
                    key={selectedCityId}
                    label="Локация (организатор)"
                    widthLimit={850}
                    initSelectedId={selectedLocationId}
                    initRefList={initData?.refs?.locations}
                    apiName2="locations"
                    filterExtraParams={{city_id: selectedCityId}}
                    showIds={true}
                    onSelect={setSelectedLocationId}
                    readOnly={readOnlyMode}
                    infoText={draftData?.organizer}
                />
                {selectedCityId && <FormCheckbox
                    controlRef={null}
                    label="Событие на внешней площадке"
                    initValue={Boolean(initData?.custom_location)}
                    onSelect={setCustomLocationIncluded}
                    tipText={"Поставьте галку, если событие проводится вне локации-организатора.\nВ этом случае необходимо будет указать место проведения."}
                    readOnly={readOnlyMode}
                />}
                {selectedCityId && customLocationIncluded && <FormInput
                    controlRef={controlRefCustomLocation}
                    label="Место проведения"
                    widthLimit={850}
                    initValue={initData?.custom_location ?? ""}
                    placeholder={"Введите название места"}
                    readOnly={readOnlyMode}
                />}
            </FormBlock>
            <FormBlock title="Время">
                <FormLinkedItemSimpleSelect
                    controlRef={controlRefExpositionType}
                    label="Время экспозиции"
                    widthLimit={150}
                    initSelectedId={tryDetectExpositionTypeFromDraft(draftData) ?? initData?.museum_details?.exposition_type}
                    options={expositionTypes}
                    onSelect={setExpositionType}
                    readOnly={readOnlyMode}
                    infoText={draftData?.exposition}
                />
                {expositionType === "temporary" && !noExpoDates && <FormDatePeriodInput
                    controlRef={controlRefTemporaryExpositionDates}
                    label={"Даты выставки"}
                    initFromValue={initExpositionType === "temporary" ? getInitialValueFromServerDate(initData?.museum_details?.exposition_start) : ""}
                    initTillValue={initExpositionType === "temporary" ? getInitialValueFromServerDate(initData?.museum_details?.exposition_end) : ""}
                    placeholder="ДД.ММ.ГГГГ"
                    validateDateFunc={text => {
                        try {
                            clientDateToServerDate(text);
                        } catch (error) {
                            return false;
                        }
                        return true;
                    }}
                    validateDatesFunc={(text1, text2) => {
                        return compareClientDates(text1, text2) <= 0;
                    }}
                    readOnly={readOnlyMode}
                />}
                {expositionType === "temporary" && <FormCheckbox
                    controlRef={null}
                    label="Даты выставки не указаны"
                    initValue={getInitialNoExpoDates(initData)}
                    onSelect={setNoExpoDates}
                    style={noExpoDates ? undefined : {marginTop:"-15px"}}
                    readOnly={readOnlyMode}
                />}
                {expositionType === "seasonal" && <FormDatePeriodInput
                    controlRef={controlRefSeasonalExpositionDates}
                    label={"Даты сезона (ежегодные)"}
                    initFromValue={initExpositionType === "seasonal" ? getInitialValueFromServerDateWoYear(initData?.museum_details?.exposition_start) : ""}
                    initTillValue={initExpositionType === "seasonal" ? getInitialValueFromServerDateWoYear(initData?.museum_details?.exposition_end) : ""}
                    placeholder="ДД.ММ"
                    validateDateFunc={text => {
                        try {
                            clientDateToServerDate(`${text}.2000`);
                        } catch (error) {
                            return false;
                        }
                        return true;
                    }}
                    readOnly={readOnlyMode}
                />}
                <FormCheckboxGrid
                    controlRef={controlRefOffDays}
                    label="Выходные дни"
                    widthLimit={550}
                    initItems={allDayOfWeeks.map(x => ({
                        ...x,
                        label: x.name,
                        checked: initData?.off_days?.includes(x.id) ?? false
                    }))}
                    readOnly={readOnlyMode}
                />
            </FormBlock>
            <FormBlock title="Авторы экспонируемых произведений">
                <FormLinkedItemListSelect
                    controlRef={controlRefArtists}
                    selectedItemsAreaType="column"
                    inputWidthLimit={500}
                    selectedItemsAreaWidthLimit={700}
                    initSelectedIds={initData?.artists?.filter(x => x.role !== "historical_figure").map(x => x.artist_id) ?? []} // TODO? упростить данные?
                    initRefList={initData?.refs?.artists}
                    apiName2="artists"
                    showIds={true}
                    itemNameFunc={item => getFullName(item.first_name, item.last_name, item.middle_name)}
                    renderItemExtraInfoFunc={renderArtistItemExtraInfo}
                    renderInitialItemExtraInfoFunc={renderInitialArtistItemExtraInfo}
                    readOnly={readOnlyMode}
                />
            </FormBlock>
            <FormBlock title="Связи">
                <FormLinkedItemListSelect
                    controlRef={controlRefLiteraryWorks}
                    label="Литературные источники"
                    inputWidthLimit={450}
                    initSelectedIds={initData?.literary_work_ids ?? []}
                    initRefList={initData?.refs?.literary_works} // TODO: должны содержать author_id (уже?)
                    apiName2="literary_works"
                    showIds={true}
                    selectedItemsAreaType="column"
                    renderItemExtraInfoFunc={renderLiteraryWorkItemExtraInfo}
                    renderInitialItemExtraInfoFunc={renderInitialLiteraryWorkItemExtraInfo}
                    dataRefs={initData?.refs}
                    readOnly={readOnlyMode}
                />
                <FormLinkedItemListSelect
                    controlRef={controlRefHistFigures}
                    label="Исторические личности"
                    selectedItemsAreaType="column"
                    inputWidthLimit={500}
                    selectedItemsAreaWidthLimit={700}
                    initSelectedIds={initData?.artists?.filter(x => x.role === "historical_figure").map(x => x.artist_id) ?? []} // TODO? упростить данные?
                    initRefList={initData?.refs?.artists}
                    apiName2="artists"
                    showIds={true}
                    itemNameFunc={item => getFullName(item.first_name, item.last_name, item.middle_name)}
                    renderItemExtraInfoFunc={renderArtistItemExtraInfo}
                    renderInitialItemExtraInfoFunc={renderInitialArtistItemExtraInfo}
                    readOnly={readOnlyMode}
                />
                <FormLinkedItemListSelect
                    controlRef={controlRefSpheres}
                    label="Сферы"
                    inputWidthLimit={450}
                    selectedItemsAreaWidthLimit={700}
                    initSelectedIds={initData?.sphere_ids ?? []}
                    initRefList={initData?.refs?.spheres}
                    apiName2="spheres"
                    showIds={true}
                    readOnly={readOnlyMode}
                />
                <FormLinkedItemListSelect
                    controlRef={controlRefTags}
                    label="Теги"
                    inputWidthLimit={450}
                    selectedItemsAreaWidthLimit={700}
                    initSelectedIds={initData?.tag_ids ?? []}
                    initRefList={initData?.refs?.tags}
                    apiName2="tags"
                    showIds={true}
                    readOnly={readOnlyMode}
                />
            </FormBlock>
            <FormBlock title="Возрастные категории">
                <FormCheckboxGrid
                    controlRef={controlRefSchoolClasses}
                    /*label="Возрастные категории"*/
                    widthLimit={670}
                    initItems={allSchoolClasses.map(x => ({
                        ...x,
                        label: `${x.id} класс`,
                        extraInfoText: `${x.ageText} лет`,
                        checked: initData?.classes?.includes(x.id) ?? false
                    }))}
                    preExtraItem={{
                        label: "Для самых маленьких",
                        extraInfoText: "0-5 лет",
                        checked: initData?.is_for_toddlers ?? false,
                    }}
                    postExtraItem={{
                        label: "Для взрослых",
                        extraInfoText: "18+ лет",
                        checked: initData?.is_for_adults ?? false,
                    }}
                    readOnly={readOnlyMode}
                />
            </FormBlock>
            <FormBlock title="Школа">
                <FormCheckbox
                    controlRef={controlRefIsSchoolProgram}
                    label="Есть в школьной программе"
                    initValue={getInitialIsSchoolProgram(initData)}
                    onSelect={value => {
                        setIsSchoolProgram(value);
                        setSchoolClassId(undefined);
                        setSchoolSubjectId(undefined);
                        setSchoolThemeId(undefined);
                    }}
                    readOnly={readOnlyMode}
                />
                {isSchoolProgram && <>
                    <FormLinkedItemSimpleSelect
                        controlRef={controlRefSchoolClass}
                        label="Класс"
                        widthLimit={150}
                        initSelectedId={schoolClassId}
                        options={allSchoolClasses}
                        onSelect={id => {
                            setSchoolClassId(id);
                            setSchoolThemeId(undefined);
                        }}
                        readOnly={readOnlyMode}
                    />
                    <FormLinkedItemSelect
                        controlRef={controlRefSchoolSubject}
                        label="Предмет"
                        widthLimit={250}
                        initSelectedId={schoolSubjectId}
                        initRefList={initData?.refs?.school_subjects}
                        apiName2="school_subjects"
                        showIds={true}
                        onSelect={id => {
                            setSchoolSubjectId(id);
                            setSchoolThemeId(undefined);
                        }}
                        readOnly={readOnlyMode}
                    />
                    <FormLinkedItemSelect
                        locked={!schoolClassId || !schoolSubjectId}
                        lockedMessage={"Сначала нужно выбрать класс и предмет!"}
                        key={schoolSubjectId + "|" + schoolClassId + "|" + isSchoolProgram}
                        controlRef={controlRefSchoolTheme}
                        label="Тема"
                        widthLimit={800}
                        initSelectedId={schoolThemeId}
                        initRefList={initData?.refs?.school_themes}
                        apiName2="school_themes"
                        specialOptions={[{id: null, name: "(не указана)"}]}
                        showIds={true}
                        filterExtraParams={{
                            subject_id: schoolSubjectId,
                            class: schoolClassId,
                        }}
                        renderItemExtraInfoFunc={renderSchoolThemeItemExtraInfo}
                        renderInitialItemExtraInfoFunc={renderInitialSchoolThemeItemExtraInfo}
                        onSelect={setSchoolThemeId}
                        readOnly={readOnlyMode}
                    />
                </>}
            </FormBlock>
            <FormBlock title="Инфо">
                <FormLinkedItemSimpleSelect
                    controlRef={controlRefMinAge}
                    label="Возрастное ограничение (официальное)"
                    widthLimit={150}
                    initSelectedId={tryDetectMinAgeFromDraft(draftData) ?? (initData ? (initData.min_age ?? -1) : undefined)}
                    options={minAgeOptions}
                    readOnly={readOnlyMode}
                    infoText={draftData?.age_limit}
                />
                <FormCheckbox
                    controlRef={controlRefIsPushkinCardAccepted}
                    label="Пушкинская карта"
                    initValue={tryDetectPushkinCardFromDraft(draftData) ?? (initData?.is_pushkin_card_accepted ?? false)}
                    readOnly={readOnlyMode}
                    infoText={draftData?.pushkin_card}
                />
                {renderRewardsSelect(controlRefAwards, "Награды", initData, "award", readOnlyMode)}
                {renderRewardsSelect(controlRefOlympiads, "Олимпиады", initData, "olympiad", readOnlyMode)}
                {renderRewardsSelect(controlRefFestivals, "Фестивали", initData, "festival", readOnlyMode)}
                <FormUrlInput
                    controlRef={controlRefUrl}
                    label="Сайт (события)"
                    widthLimit={500}
                    initValue={draftData?.event_url ?? (initData?.url ?? "")}
                    placeholder="https://..."
                    readOnly={readOnlyMode}
                    infoText={draftData?.event_url}
                />
            </FormBlock>
            <FormBlock title="Варианты посещений">
                <FormMuseumEventVisitsEditor
                    controlRef={controlRefVisitTypes}
                    initPresets={initData?.visit_types ?? []}
                    initDataRefs={initData?.refs}
                    readOnly={readOnlyMode}
                    infoText={draftData?.visit_types}
                />
            </FormBlock>
            {initData && <FormBlock title="Специальные">
                <FormCheckbox
                    controlRef={controlRefArchived}
                    label="Архивная карточка"
                    initValue={Boolean(initData?.deleted_at)}
                    tipText={"Архивные карточки хранятся в базе данных, но не используются в работе.\nПереключая данную галку, можно делать карточку архивной и наоборот.\nЧтобы применить выбор, нужно также выполнить Сохранение карточки."}
                    readOnly={readOnlyMode}
                />
            </FormBlock>}
        </BaseSimpleItemEditorForm>
    );
};

function renderRewardsSelect(controlRef, label, initData, kind, readOnly) {
    return <FormRewardsSelect
        controlRef={controlRef}
        selectedItemsAreaType="column"
        label={label}
        inputWidthLimit={450}
        selectedItemsAreaWidthLimit={700}
        initSelectedOptions={initData?.reward_years?.filter(x => getInitialRewardKindById(x.reward_id, initData) === kind).map(x => ({
            id: x.reward_id,
            year1: x.year_start ?? null,
            year2: x.year_end ?? null,
        })) ?? []}
        initRefList={initData?.refs?.rewards}
        apiName2="rewards"
        showIds={true}
        filterExtraParams={{kinds: kind}}
        readOnly={readOnly}
    />
}

function getInitialRewardKindById(rewardId, initData) {
    const result = initData?.refs?.rewards?.[rewardId]?.kind ?? "";
    if (!result)
        console.error(`can't detect reward kind from refs for rewardId=${rewardId}}`);
    return result;
}

const expositionTypes = [
    {id: "permanent", name: "постоянно"},
    {id: "seasonal", name: "сезонно"},
    {id: "temporary", name: "выставка"},
];

const minAgeOptions = [
    {id: -1, name: "(не указано)"},
    ...allAges
];

function getAgeData(ageControlRef) {
    const selectedId = ageControlRef.current.getSelectedId();
    return selectedId === -1 ? null : selectedId;
}

function getInitialValueFromServerDate(src) {
    return src ? serverDateToClientDate(src) : "";
}

function getInitialValueFromServerDateWoYear(src) {
    if (!src)
        return "";

    const regex = /^(\d\d\d\d)-(\d\d)-(\d\d)$/;
    const match = regex.exec(src);
    if (!match)
        throw new Error(`invalid serverDate format: ${src}`);
    return `${match[3]}.${match[2]}`;
}

function compareClientDates(text1, text2) {
    const regex = /^\s*(\d{1,2})\s*\.\s*(\d{1,2})\s*\.\s*(\d{1,4})\s*$/;
    const match1 = regex.exec(text1);
    const match2 = regex.exec(text2);
    const year1 = parseInt(match1[3]);
    const year2 = parseInt(match2[3]);
    if (year1 !== year2)
        return year1 - year2;
    const month1 = parseInt(match1[2]);
    const month2 = parseInt(match2[2]);
    if (month1 !== month2)
        return month1 - month2;
    const day1 = parseInt(match1[1]);
    const day2 = parseInt(match2[1]);
    return day1 - day2;
}

function renderLiteraryWorkItemExtraInfo(item, className, dataRefs) {
    return <span className={className}>{getAuthorName(item.author_id, dataRefs)}</span>
}

function getAuthorName(authorId, dataRefs) {
    if (authorId === null)
        return "(без автора)";

    const author = dataRefs?.authors?.[authorId];
    return author ? getShortName(author.first_name, author.last_name, author.middle_name) : "?";
}

function renderArtistItemExtraInfo(item, className, dataRefs) {
    return <span className={className}>{allArtistRoles.find(x => x.id === item.main_role)?.name ?? "?"}</span>
}

function renderSchoolThemeItemExtraInfo(item, className, dataRefs) {
    const classItem = allSchoolClasses.find(x => x.id === item.class);
    if (!classItem)
        return null;
    return <span className={className} style={{textWrap:"nowrap"}}>{classItem.name}</span>
}

function renderCityItemExtraInfo(item, className, dataRefs) {
    if (item.is_region_city)
        return null;

    const regionTitle = dataRefs?.regions?.[item.region_id]?.name ?? "?";
    return <span className={className}>{regionTitle}</span>
}

function getInitialNoExpoDates(initData) {
    const details = initData?.museum_details;

    if (details?.exposition_type !== "temporary")
        return false;

    return !details.exposition_start && !details.exposition_end;
}

function getInitialIsSchoolProgram(initData) {
    return initData?.museum_details?.is_school_program ?? false;
}

function getInitialSchoolClassId(initData) {
    const initialThemeId = getInitialSchoolThemeId(initData);

    if (initialThemeId)
        return initData?.refs?.school_themes?.[initialThemeId]?.class ?? null;

    return getInitialIsSchoolProgram(initData) ? (initData?.museum_details?.school_class ?? null) : undefined;
}

function getInitialSchoolSubjectId(initData) {
    return getInitialIsSchoolProgram(initData) ? (initData?.museum_details?.school_subject_id ?? null) : undefined;
}

function getInitialSchoolThemeId(initData) {
    return getInitialIsSchoolProgram(initData) ? (initData?.museum_details?.school_theme_id ?? null) : undefined;
}

// TODO?

function tryDetectPushkinCardFromDraft(draftData) {
    if (!draftData || !draftData.pushkin_card)
        return undefined;

    return draftData.pushkin_card.trim().toLocaleLowerCase() === "да";
}

function tryDetectExpositionTypeFromDraft(draftData) {
    if (!draftData || !draftData.exposition)
        return undefined;

    if (draftData.exposition.trim().toLocaleLowerCase().includes("постоян"))
        return "permanent";

    return undefined;
}

function tryDetectMinAgeFromDraft(draftData) {
    if (!draftData || !draftData.age_limit)
        return undefined;

    const text = draftData.age_limit.trim();
    const regex = /^(\d{1,2})\s*\+$/;
    const match = regex.exec(text);
    if (match) {
        const num = parseInt(match[1]);
        if (!isNaN(num) && num >= 0 && num <= 18)
            return num;
    }

    return undefined;
}

export default EventsMuseumItemForm;